import { CONTACT_EMAIL, LegalPage, List, Section } from './LegalPage';

export function PrivacyPolicy() {
  return (
    <LegalPage
      title="Privacy Policy"
      description="What ClientBridge collects, where it is stored, who can see it, and how to delete it."
      // Revised when account deletion moved in-app; the Terms are unchanged and keep
      // the shared date.
      effectiveDate="8 August 2026"
    >
      <Section heading="The short version">
        <p>
          ClientBridge stores the interactions you log and the account you sign
          in with. Nothing else. We do not sell your data, we do not show ads,
          and we do not use your client records to train anything. Every row in
          our database is locked to the account that created it.
        </p>
      </Section>

      <Section heading="What we collect">
        <List
          items={[
            <>
              <strong>Account details.</strong> When you sign in with Apple or
              Google we receive your email address and, if you allow it, your
              name. We never see your password — authentication happens with
              Apple or Google, not with us. If you use Apple’s{' '}
              <strong>Hide My Email</strong>, we only ever store the relay
              address.
            </>,
            <>
              <strong>Your industry.</strong> The one selection you make during
              onboarding, used to shape your timeline and reminders.
            </>,
            <>
              <strong>The interactions you log.</strong> The client name, phone
              number, note, and timestamp of each entry you create.
            </>,
          ]}
        />
        <p>
          We do not collect location, contacts, photos, advertising identifiers,
          or analytics about how you use the app.
        </p>
      </Section>

      <Section heading="Information about other people">
        <p>
          The interactions you log are usually about someone else — your client.
          You decide what to write down. In data-protection terms{' '}
          <strong>you are the controller</strong> of that information and
          ClientBridge is the processor: we hold it on your behalf and act on
          your instructions.
        </p>
        <p>
          You are responsible for having a lawful basis to record a client’s
          details, and for only recording what you actually need. Please do not
          store special-category information (health, financial account numbers,
          government identifiers) in a note — the app is not designed for it.
        </p>
      </Section>

      <Section heading="Where your data lives">
        <p>
          Your data is stored in <strong>Supabase</strong> (PostgreSQL), hosted
          on AWS in the <strong>ap-south-1 region (Mumbai, India)</strong>. If
          you are outside India, this means your data is transferred to and
          processed there.
        </p>
        <p>
          Access is enforced at the database with row-level security: every query
          is scoped to the signed-in account, so one user’s rows are unreadable
          to any other user — including through our own public API keys.
        </p>
      </Section>

      <Section heading="Who we share it with">
        <p>
          Nobody, other than the infrastructure providers we need to run the
          service:
        </p>
        <List
          items={[
            <>
              <strong>Supabase</strong> — database, authentication, and hosting.
            </>,
            <>
              <strong>Apple</strong> and <strong>Google</strong> — only to verify
              your identity when you sign in.
            </>,
          ]}
        />
        <p>
          We do not sell personal information, and we do not share it for
          advertising. We will disclose data if we are legally compelled to, and
          will tell you unless we are prohibited from doing so.
        </p>
      </Section>

      <Section heading="How long we keep it">
        <p>
          Your interactions stay until you delete them or delete your account.
          When your account is deleted, every row that belongs to it is removed
          along with it — deletion cascades from your account to your profile and
          to all of your interactions. Backups age out on our provider’s
          schedule.
        </p>
      </Section>

      <Section heading="Your choices">
        <List
          items={[
            <>
              <strong>Delete an interaction</strong> at any time from the app.
            </>,
            <>
              <strong>Delete your account and everything in it</strong> — open
              the <strong>Settings</strong> tab in the app and tap{' '}
              <strong>Delete account</strong>. It takes effect immediately: your
              profile and every interaction you have logged are erased, and you
              are signed out. Nothing is retained for us to restore, so this
              cannot be undone. You do not need to ask us, and we cannot do it
              on your behalf any faster.
            </>,
            <>
              <strong>Get a copy of your data</strong> — ask us and we will send
              you an export.
            </>,
            <>
              <strong>Revoke sign-in access</strong> from your Apple ID or Google
              Account settings at any time.
            </>,
          ]}
        />
      </Section>

      <Section heading="Children">
        <p>
          ClientBridge is a tool for running a business and is not directed at
          children. We do not knowingly collect information from anyone under 16.
        </p>
      </Section>

      <Section heading="Changes">
        <p>
          If we change this policy in a way that materially affects you, we will
          update the effective date above and tell you in the app before the
          change takes effect.
        </p>
      </Section>

      <Section heading="Contact">
        <p>
          Questions, requests, or complaints:{' '}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>
      </Section>
    </LegalPage>
  );
}
